import {Button, CheckboxTree, CheckboxTreeOptionType, Search, Separator} from 'platform/components';
import {Box, Heading, Space, Show, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode, useState, useTransition} from 'react';

import {isNotEmpty, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {
  convertStringToCamelCase,
  Nullish,
  sortByAsciiNumberLast,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {FeatureSubCategory} from '../../types/FeatureSubCategory';
import {groupFeaturesBySubCategory} from '../../utils/groupFeaturesBySubCategory';
import {FeaturePickerItem} from './types/FeaturePickerItem';
import {filterFeatures} from './utils/filterFeatures';
import {handleCheckboxTreeSelect} from './utils/handleCheckboxTreeSelect';
import {mapFeaturesRecursively} from './utils/mapFeaturesRecursively';

interface FeaturesPickerProps extends TestIdProps {
  categories: FeaturePickerItem[];
  features: FeaturePickerItem[];
  selected?: Nullish | string[];
  onFeatureSelect: (selected: string[]) => void;
  /**
   * To select more than one subNote of the tree
   */
  allowSubItemMultiselect?: boolean;
  /**
   * Allow the option to expand all features
   */
  isExpandable?: boolean;
  /**
   * Forces all features to be in single column instead of two columns
   */
  forceSingleColumn?: boolean;
  /**
   * Catalogue subcategories. When provided, features of each category are grouped under subcategory
   * headings (features without a subcategory go to "Other"). Grouping is visual only.
   */
  subCategories?: FeatureSubCategory[];
}

export function FeaturesPicker(props: FeaturesPickerProps) {
  const {
    categories,
    features,
    selected = [],
    onFeatureSelect,
    allowSubItemMultiselect = false,
    isExpandable = false,
    forceSingleColumn = false,
    subCategories,
  } = props;

  const [, startTransition] = useTransition();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(!isExpandable);
  const [filteredFeatures, setFilteredFeatures] = useState<FeaturePickerItem[] | null>(null);

  const handleClickSingle = (value: string | null) => {
    if (!allowSubItemMultiselect && isNotNil(value)) {
      handleCheckboxTreeSelect({
        value,
        selected,
        onFeatureSelect,
        features,
      });
    }
  };
  const handleClickMultiple = (value: string[] | null) => {
    if (allowSubItemMultiselect) {
      onFeatureSelect(value ?? []);
    }
  };

  const handleSearch = (newSearchQuery: string | null) => {
    setSearchQuery(newSearchQuery);

    if (!newSearchQuery) {
      setFilteredFeatures(null);
      return;
    }

    startTransition(() => {
      const filtered = filterFeatures(
        (item, level) =>
          (level > 0 ? item?.shortLabel || item?.label : item?.label)
            ?.toLowerCase()
            .trim()
            .includes(String(newSearchQuery.toLowerCase().trim())),
        features
      );

      setFilteredFeatures(filtered);
    });
  };

  const renderCategory = (category: FeaturePickerItem, level = 0, last = false): ReactNode => (
    <div
      key={category.label}
      data-testid={suffixTestId(
        `${convertStringToCamelCase(category.value)}-categoryWrapper`,
        props
      )}
    >
      {renderFeaturesInCategory(category, level, last)}

      {!!category.children?.length &&
        category.children.map((child) => (
          <div key={child.value}>{renderCategory(child, level + 1)}</div>
        ))}
    </div>
  );

  const renderFeatureTrees = (category: FeaturePickerItem, items: CheckboxTreeOptionType[]) => (
    <CategoryFeaturesWrapper
      data-testid={suffixTestId(
        `${convertStringToCamelCase(category.value)}-categoryListWrapper`,
        props
      )}
      columnCount={forceSingleColumn ? 1 : 3}
    >
      {items.map((item) => (
        <Box
          padding={1}
          overflow="hidden"
          key={item.value}
          data-testid={suffixTestId(
            `${convertStringToCamelCase(category.value)}-checkBoxTreeWrapper`,
            props
          )}
        >
          <CheckboxTree
            options={[item]}
            value={selected ?? []}
            onChange={(value) => handleClickMultiple(value)}
            onSingleValueChange={(value) => handleClickSingle(value)}
            data-testid={suffixTestId(
              `${convertStringToCamelCase(category.value)}-checkBoxTree`,
              props
            )}
          />
        </Box>
      ))}
    </CategoryFeaturesWrapper>
  );

  const renderSubCategoryGroups = (
    category: FeaturePickerItem,
    items: FeaturePickerItem[],
    catalogueSubCategories: FeatureSubCategory[]
  ) => {
    const groups = groupFeaturesBySubCategory({
      items,
      categoryKey: category.value,
      subCategories: catalogueSubCategories,
      getSubCategory: (item) => item.subCategory,
    });
    const hasHeadings = groups.some((group) => !group.isOther);

    return (
      <VStack spacing={3}>
        {groups.map((group) => (
          <VStack
            key={group.key}
            spacing={2}
            data-testid={suffixTestId(
              `${convertStringToCamelCase(category.value)}-subCategory-${convertStringToCamelCase(group.key)}`,
              props
            )}
          >
            <Show when={hasHeadings}>
              <Heading size={6} color="secondary">
                {group.label ?? i18n.t('entity.vehicle.labels.otherLabel')}
              </Heading>
            </Show>
            {renderFeatureTrees(category, group.items.map(mapFeaturesRecursively))}
          </VStack>
        ))}
      </VStack>
    );
  };

  const renderFeaturesInCategory = (
    category: FeaturePickerItem,
    level = 0,
    last = false
  ): ReactNode => {
    const categoryKey = category?.value as string;
    const filteredItems = (filteredFeatures ?? features).filter((feature) =>
      feature.categories?.find((category) => category === categoryKey)
    );
    const sortedItems = filteredItems.toSorted((a, b) => sortByAsciiNumberLast(a.label, b.label));

    if (sortedItems.length === 0) {
      return null;
    }

    return (
      <>
        {level === 0 ? (
          <Box paddingBottom={3}>
            <Heading size={4} color="secondary">
              {category.label}
            </Heading>
          </Box>
        ) : (
          <Box paddingVertical={3}>
            <Heading size={5} color="tertiary">
              {category.label}
            </Heading>
          </Box>
        )}

        {isNotNil(subCategories) && isNotEmpty(subCategories)
          ? renderSubCategoryGroups(category, sortedItems, subCategories)
          : renderFeatureTrees(category, sortedItems.map(mapFeaturesRecursively))}

        <Show when={!last}>
          <Separator />
        </Show>
      </>
    );
  };

  return (
    <div>
      <Search
        data-testid={suffixTestId('search', props)}
        value={searchQuery}
        onChange={handleSearch}
      />

      <Show when={isExpandable}>
        <>
          <Space vertical={3} />
          <Button
            data-testid={suffixTestId('toggleExpand', props)}
            variant="link"
            rightIcon={isExpanded ? 'hardware/keyboard_arrow_up' : 'hardware/keyboard_arrow_down'}
            onClick={() => setIsExpanded(!isExpanded)}
            title={
              isExpanded
                ? i18n.t('entity.vehicle.actions.hideAllFeatures')
                : i18n.t('entity.vehicle.actions.showAllFeatures')
            }
          />
        </>
      </Show>

      <Show when={isExpanded || filteredFeatures}>
        <>
          <Space vertical={3} />
          {categories.map((category, i) =>
            renderCategory(category, 0, categories.length === i + 1)
          )}
        </>
      </Show>
    </div>
  );
}

const CategoryFeaturesWrapper = styled.div<{columnCount: number}>`
  flex: 3;
  column-count: ${({columnCount}) => columnCount};
`;
